import type { PaymentMethod } from "@/lib/types";

export const paymentMethods: PaymentMethod[] = [
  {
    id: "pm-swish",
    name: "Swish",
    slug_sv: "swish-casino",
    slug_en: "swish-casinos",
    type: "mobile",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: false,
    requires_bankid: true,
    supports_deposit: true,
    supports_withdrawal: true,
    typical_speed: "Sekunder till minuter",
    description_sv:
      "Swish är den svenska mobilbetalningstjänsten ägd av de stora svenska bankerna. Den används av över åtta miljoner svenskar och har blivit standardvalet för insättningar hos casinon med svensk licens.",
    description_en:
      "Swish is the Swedish mobile payment service owned by the major Swedish banks. It is used by over eight million Swedes and has become the default deposit choice at Swedish-licensed casinos.",
    pros_sv: [
      "Sekundsnabba insättningar med BankID-bekräftelse",
      "Inga avgifter på insättning hos casinon",
      "Direkt koppling till ditt svenska bankkonto",
      "Samma flöde som du använder dagligen",
    ],
    pros_en: [
      "Second-fast deposits confirmed via BankID",
      "No deposit fees at casinos",
      "Direct connection to your Swedish bank account",
      "Same flow you use daily",
    ],
    cons_sv: [
      "Bara tillgängligt hos casinon med svensk licens",
      "Kräver svenskt bankkonto och BankID",
      "Vissa casinon stödjer Swish endast för insättning",
    ],
    cons_en: [
      "Only available at Swedish-licensed casinos",
      "Requires a Swedish bank account and BankID",
      "Some casinos support Swish only for deposits",
    ],
    long_sections_sv: [
      {
        heading: "Hur Swish-insättning fungerar",
        body: "När du väljer Swish som betalningsmetod öppnas BankID-appen automatiskt. Du anger beloppet, signerar med BankID, och pengarna är på spelkontot inom sekunder. Casinot ser aldrig ditt kortnummer eller ditt bankkonto — Swish är en mellanlösning som krypterar transaktionen genom Bankgirot och ditt egna bankkonto.",
      },
      {
        heading: "Uttag med Swish",
        body: "Vissa licensierade casinon erbjuder Swish-uttag direkt till mobilen. Det är snabbare än traditionell banköverföring eftersom det går genom Swish-systemet istället för att vänta på bankens batch-körningar. Maxbeloppet för ett enskilt Swish-uttag är dock ofta lägre — typiskt 15 000 kr per dygn.",
      },
      {
        heading: "Säkerhet",
        body: "Swish är en av Europas säkraste mobilbetalningar eftersom varje transaktion bekräftas via BankID. Du kan inte godkänna en betalning utan att fysiskt signera med ditt BankID. Det skyddar både dig och casinot mot bedrägeri.",
      },
    ],
    long_sections_en: [
      {
        heading: "How a Swish deposit works",
        body: "When you select Swish as your payment method the BankID app opens automatically. You enter the amount, sign with BankID, and the money lands on the casino account within seconds. The casino never sees your card number or bank account — Swish is an intermediary that encrypts the transaction through Bankgirot and your own bank.",
      },
      {
        heading: "Swish withdrawals",
        body: "Some licensed casinos offer Swish withdrawals straight to your phone. It is faster than traditional bank transfer because it bypasses the bank's batch processing. The per-withdrawal cap is usually lower — typically 15,000 SEK per 24 hours.",
      },
      {
        heading: "Security",
        body: "Swish is one of Europe's most secure mobile payments because every transaction is confirmed via BankID. You cannot authorise a payment without physically signing with your BankID. That protects both you and the casino from fraud.",
      },
    ],
    published: true,
  },
  {
    id: "pm-trustly",
    name: "Trustly",
    slug_sv: "trustly",
    slug_en: "trustly",
    type: "bank",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: true,
    supports_deposit: true,
    supports_withdrawal: true,
    typical_speed: "Vanligtvis under en timme",
    description_sv:
      "Trustly är ett svenskt fintech-bolag som låter dig betala direkt från ditt bankkonto utan att registrera ett konto hos Trustly. På casinon är det en av de snabbaste betalningsmetoderna både för insättning och uttag.",
    description_en:
      "Trustly is a Swedish fintech that lets you pay directly from your bank account without registering with Trustly. At casinos it is one of the fastest deposit and withdrawal methods.",
    long_sections_sv: [
      {
        heading: "Pay-and-play med Trustly",
        body: "Trustly är hjärtat i de flesta pay-and-play-casinon. Istället för att registrera ett konto med användarnamn och lösenord loggar du in på din bank via Trustly, sätter in pengar, och spelar — allt med BankID. Operatören identifierar dig genom Trustlys verifiering och du behöver aldrig fylla i något formulär.",
      },
      {
        heading: "Hastighet",
        body: "Insättningar är på spelkontot inom 30 sekunder. Uttag är ofta tillbaka på din bank inom 0–60 minuter — vissa svenska banker har dock batch-körningar nattetid och helger som kan förlänga tiden. Trustly har ingen egen avgift på spelvärlden.",
      },
      {
        heading: "Tillgänglighet",
        body: "Trustly fungerar både hos casinon med svensk licens och utan. På utländska sajter används Trustly oftast som verifieringsverktyg för pay-and-play. På svenska licensierade sajter fungerar Trustly som ett alternativ till Swish och Zimpler.",
      },
    ],
    long_sections_en: [
      {
        heading: "Pay-and-play with Trustly",
        body: "Trustly is the heart of most pay-and-play casinos. Instead of registering a username and password, you log in to your bank via Trustly, deposit, and play — all signed with BankID. The operator identifies you through Trustly's verification so you never fill in a form.",
      },
      {
        heading: "Speed",
        body: "Deposits land within 30 seconds. Withdrawals are often back at your bank within 0–60 minutes — but some Swedish banks have nightly or weekend batch processing that can extend the time. Trustly has no fee for the player in casino flows.",
      },
      {
        heading: "Availability",
        body: "Trustly works at both Swedish-licensed and offshore casinos. At foreign sites it most often acts as the pay-and-play verification layer. At Swedish-licensed sites it sits alongside Swish and Zimpler as a primary option.",
      },
    ],
    pros_sv: [
      "Fungerar på i princip alla svenska casinon",
      "Snabba uttag, ofta inom en timme",
      "Pay-and-play utan att skapa konto",
      "BankID-säkerhet på varje transaktion",
    ],
    pros_en: [
      "Works at virtually every Swedish casino",
      "Fast withdrawals, usually within an hour",
      "Pay-and-play without creating an account",
      "BankID security on every transaction",
    ],
    cons_sv: [
      "Vissa banker har batch-körningar som fördröjer uttag",
      "Maxbelopp varierar mellan operatörer",
    ],
    cons_en: [
      "Some banks use batch processing that delays withdrawals",
      "Max amounts vary between operators",
    ],
    published: true,
  },
  {
    id: "pm-bankid",
    name: "BankID",
    slug_sv: "bankid",
    slug_en: "bankid",
    type: "mobile",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: true,
    supports_deposit: false,
    supports_withdrawal: false,
    typical_speed: "Sekunder",
    description_sv:
      "BankID är inte ett betalmedel i sig utan en e-legitimation som används för att signera betalningar via Swish, Trustly och Zimpler hos svenska casinon. Det är obligatoriskt för registrering hos varje svenskt licensierat casino.",
    description_en:
      "BankID is not a payment method itself but an e-ID used to sign payments via Swish, Trustly and Zimpler at Swedish casinos. It is mandatory for registration at every Swedish-licensed casino.",
    long_sections_sv: [
      {
        heading: "Vad BankID är",
        body: "BankID är Sveriges nationella e-legitimation, lanserad 2003 och idag använd av över åtta miljoner svenskar. Den finns som mobilapp, säkerhetsdosa eller på fil. På casinon används BankID för identifiering vid registrering och för att signera varje insättning och uttag som går genom Swish, Trustly eller Zimpler.",
      },
      {
        heading: "Varför svensk licens kräver BankID",
        body: "Spelinspektionen kräver BankID för att säkerställa att varje spelkonto är personligt knutet till en folkbokförd svensk medborgare. Det förhindrar barnspelande, dubbla konton och möjliggör Spelpaus-anslutningen. Det är en av kärnpelarna i den svenska spellicensen.",
      },
    ],
    long_sections_en: [
      {
        heading: "What BankID is",
        body: "BankID is Sweden's national e-ID, launched in 2003 and now used by over eight million Swedes. It comes as a mobile app, a security token or on file. At casinos BankID is used for identity verification at registration and to sign every deposit and withdrawal that runs through Swish, Trustly or Zimpler.",
      },
      {
        heading: "Why a Swedish license requires BankID",
        body: "Spelinspektionen requires BankID to ensure each gambling account is personally tied to a Swedish-registered citizen. It prevents underage gambling, duplicate accounts and enables the Spelpaus connection. It is a core pillar of the Swedish gambling license.",
      },
    ],
    pros_sv: ["Maximal säkerhet", "Snabbt — sekunder", "Funkar för alla licensierade sajter"],
    pros_en: ["Maximum security", "Fast — seconds", "Works at every licensed site"],
    cons_sv: ["Bara för svenska medborgare med folkbokföring", "Kräver mobilen"],
    cons_en: ["Swedish-registered citizens only", "Requires the mobile device"],
    published: true,
  },
  {
    id: "pm-zimpler",
    name: "Zimpler",
    slug_sv: "zimpler",
    slug_en: "zimpler",
    type: "mobile",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: true,
    supports_deposit: true,
    supports_withdrawal: true,
    typical_speed: "Snabbt, ofta minuter",
    description_sv:
      "Zimpler erbjuder pay-and-play med BankID och används både hos svenska casinon med licens och hos utländska sajter.",
    description_en:
      "Zimpler offers pay-and-play with BankID and is used at both Swedish-licensed and offshore sites.",
    published: true,
  },
  {
    id: "pm-brite",
    name: "Brite",
    slug_sv: "brite",
    slug_en: "brite",
    type: "bank",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: true,
    supports_deposit: true,
    supports_withdrawal: true,
    typical_speed: "Snabbt",
    description_sv:
      "Brite är en svensk pay-and-play-tjänst som används främst hos casinon utan svensk licens.",
    description_en:
      "Brite is a Swedish pay-and-play service used primarily by casinos without a Swedish license.",
    published: true,
  },
  {
    id: "pm-klarna",
    name: "Klarna",
    slug_sv: "klarna",
    slug_en: "klarna",
    type: "bank",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: true,
    supports_deposit: true,
    supports_withdrawal: false,
    description_sv:
      "Klarna används sällan vid svenska casinon med licens, men kan finnas på vissa internationella sajter.",
    description_en:
      "Klarna is rarely used at Swedish-licensed casinos but may appear at certain international sites.",
    published: true,
  },
  {
    id: "pm-visa",
    name: "Visa",
    slug_sv: "visa",
    slug_en: "visa",
    type: "card",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv: "Visa-kort accepteras av de flesta casinon. Uttag tar ofta längre tid.",
    description_en: "Visa cards are accepted by most casinos. Withdrawals can take longer.",
    published: true,
  },
  {
    id: "pm-mastercard",
    name: "Mastercard",
    slug_sv: "mastercard",
    slug_en: "mastercard",
    type: "card",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv: "Mastercard fungerar på de flesta svenska och internationella casinon.",
    description_en: "Mastercard works at most Swedish and international casinos.",
    published: true,
  },
  {
    id: "pm-skrill",
    name: "Skrill",
    slug_sv: "skrill",
    slug_en: "skrill",
    type: "ewallet",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv:
      "Skrill är en e-wallet som används främst hos internationella casinon utan svensk licens.",
    description_en:
      "Skrill is an e-wallet primarily used at international casinos without a Swedish license.",
    published: true,
  },
  {
    id: "pm-neteller",
    name: "Neteller",
    slug_sv: "neteller",
    slug_en: "neteller",
    type: "ewallet",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv: "Neteller används huvudsakligen vid internationella sajter.",
    description_en: "Neteller is mainly used at international sites.",
    published: true,
  },
  {
    id: "pm-apple-pay",
    name: "Apple Pay",
    slug_sv: "apple-pay",
    slug_en: "apple-pay",
    type: "mobile",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: false,
    description_sv:
      "Apple Pay är ett snabbt sätt att sätta in pengar på casinon som stödjer det.",
    description_en: "Apple Pay is a quick deposit method at casinos that support it.",
    published: true,
  },
  {
    id: "pm-google-pay",
    name: "Google Pay",
    slug_sv: "google-pay",
    slug_en: "google-pay",
    type: "mobile",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: false,
    description_sv: "Google Pay för snabba insättningar på Android-enheter.",
    description_en: "Google Pay for fast deposits on Android devices.",
    published: true,
  },
  {
    id: "pm-paysafecard",
    name: "Paysafecard",
    slug_sv: "paysafecard",
    slug_en: "paysafecard",
    type: "voucher",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: false,
    description_sv:
      "Paysafecard är en förbetald voucher. Används främst utanför svensk licens.",
    description_en: "Paysafecard is a prepaid voucher, mostly used outside Swedish license.",
    published: true,
  },
  {
    id: "pm-bitcoin",
    name: "Bitcoin",
    slug_sv: "bitcoin",
    slug_en: "bitcoin",
    type: "crypto",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv:
      "Bitcoin används endast på kryptocasinon. Inga svenska casinon med licens stöder krypto.",
    description_en:
      "Bitcoin is used only at crypto casinos. No Swedish-licensed casinos support crypto.",
    published: true,
  },
  {
    id: "pm-eth",
    name: "Ethereum",
    slug_sv: "ethereum",
    slug_en: "ethereum",
    type: "crypto",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv: "Ethereum erbjuds av flera kryptocasinon.",
    description_en: "Ethereum is offered by several crypto casinos.",
    published: true,
  },
  {
    id: "pm-usdt",
    name: "USDT (Tether)",
    slug_sv: "usdt",
    slug_en: "usdt",
    type: "crypto",
    available_in_sweden: true,
    works_with_swedish_license: false,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    description_sv: "USDT är en stablecoin som används vid kryptocasinon.",
    description_en: "USDT is a stablecoin used at crypto casinos.",
    published: true,
  },
  {
    id: "pm-bank-transfer",
    name: "Banköverföring",
    slug_sv: "bankoverforing",
    slug_en: "bank-transfer",
    type: "bank",
    available_in_sweden: true,
    works_with_swedish_license: true,
    works_without_swedish_license: true,
    requires_bankid: false,
    supports_deposit: true,
    supports_withdrawal: true,
    typical_speed: "1–3 bankdagar",
    description_sv:
      "Vanlig banköverföring fungerar men är långsammare än Trustly och Swish.",
    description_en:
      "Standard bank transfer works but is slower than Trustly and Swish.",
    published: true,
  },
];

export function findPayment(slugOrName: string): PaymentMethod | undefined {
  return paymentMethods.find(
    (m) =>
      m.slug_sv === slugOrName ||
      m.slug_en === slugOrName ||
      m.name.toLowerCase() === slugOrName.toLowerCase(),
  );
}
